import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useLocationContext } from '@/components/layout/LocationLayout'
import { useLocation as useLocationData, useUpdateLocation } from '@/hooks/useLocations'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'coming_soon', label: 'Coming Soon' },
]

const locationGeneralSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  airport_code: z.string().min(1, 'Airport code is required'),
  iata_code: z.string().optional().or(z.literal('')),
  timezone: z.string().optional().or(z.literal('')),
  status: z.string().optional().or(z.literal('')),
  phone_main: z.string().optional().or(z.literal('')),
  email_general: z.string().email('Invalid email').optional().or(z.literal('')),
  address_line1: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state_province: z.string().optional().or(z.literal('')),
  postal_code: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
})

type LocationGeneralFormValues = z.infer<typeof locationGeneralSchema>

export function LocationGeneralTab() {
  const { locationId } = useLocationContext()
  const { data: location, isLoading } = useLocationData(locationId)
  const updateLocation = useUpdateLocation()

  const form = useForm<LocationGeneralFormValues>({
    resolver: zodResolver(locationGeneralSchema),
    defaultValues: {
      name: '',
      airport_code: '',
      iata_code: '',
      timezone: '',
      status: '',
      phone_main: '',
      email_general: '',
      address_line1: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: '',
    },
  })

  // Populate form when location data loads
  useEffect(() => {
    if (location) {
      form.reset({
        name: location.name || '',
        airport_code: location.airport_code || '',
        iata_code: location.iata_code || '',
        timezone: location.timezone || '',
        status: location.status || 'active',
        phone_main: location.phone_main || '',
        email_general: location.email_general || '',
        address_line1: location.address_line1 || '',
        city: location.city || '',
        state_province: location.state_province || '',
        postal_code: location.postal_code || '',
        country: location.country || '',
      })
    }
  }, [location, form])

  const isDirty = form.formState.isDirty

  const handleSubmit = form.handleSubmit((data) => {
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, value === '' ? null : value])
    )
    updateLocation.mutate({
      id: locationId,
      data: cleanData as Record<string, string | null>,
    })
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Identity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Location Identity</CardTitle>
            <CardDescription>Basic identification for this FBO location</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Burke Lakefront Airport" {...field} disabled={updateLocation.isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="airport_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Airport Code</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., KBKL" {...field} disabled={updateLocation.isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="iata_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IATA Code</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., BKL" {...field} disabled={updateLocation.isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="space-y-2">
              <FormLabel className="text-sm font-medium">Location Code</FormLabel>
              <Input value={location?.location_code || ''} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">Location code cannot be changed after creation</p>
            </div>
          </CardContent>
        </Card>

        {/* Operations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Operations</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timezone</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''} disabled={updateLocation.isPending}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''} disabled={updateLocation.isPending}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="phone_main"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="+1 (555) 123-4567" {...field} disabled={updateLocation.isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email_general"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="info@airport.com" type="email" {...field} disabled={updateLocation.isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="address_line1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Airport Blvd" {...field} disabled={updateLocation.isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} disabled={updateLocation.isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state_province"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State / Province</FormLabel>
                    <FormControl>
                      <Input placeholder="State" {...field} disabled={updateLocation.isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input placeholder="10001" {...field} disabled={updateLocation.isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input placeholder="US" {...field} disabled={updateLocation.isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Coordinates (read-only) */}
        {location?.latitude && location?.longitude && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Coordinates</CardTitle>
              <CardDescription>Coordinates are managed via the Location Designer</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <FormLabel className="text-sm font-medium">Latitude</FormLabel>
                <Input value={location.latitude} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <FormLabel className="text-sm font-medium">Longitude</FormLabel>
                <Input value={location.longitude} disabled className="bg-muted" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => form.reset()}
            disabled={!isDirty || updateLocation.isPending}
          >
            Reset
          </Button>
          <Button type="submit" disabled={!isDirty || updateLocation.isPending}>
            {updateLocation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
